import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Lock, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../theme/useTheme";
import { useAuth } from "../../contexts/AuthContext";
import { hasActiveSubscription } from "../../utils/subscription";
import { getUserPlan } from "../../utils/planAccess";
import PlanBanner from "../astria/PlanBanner";
import { api } from "../../services/api";
import {
  WorldRegion,
  getRegionsByContinent,
  formatRegionPrice,
  isSameRegion,
} from "../../utils/regions";

const COLORS = {
  amethyst: "#A78BFF",
  darkAmethyst: "#F7F3FA",
  roseGold: "#C8BFD1",
  ink: "#C8BFD1",
  muted: "#9A90A8",
  hairline: "rgba(255,255,255,0.08)",
  cream: "#221A29",
};

// Same purple-to-rose-gold gradient the sidebar's premium upgrade CTA uses,
// so every "unlock premium access" button reads as one brand family.
const CTA_GRADIENT = COLORS.amethyst;

type BillingCycle = "monthly" | "annual";

interface RegionSub {
  region: string;
  planName: string | null;
  cadence: "monthly" | "annual";
  startDate: string;
  endDate: string;
  status: string;
}

// Thailand is the free tier every account starts on.
const FREE_TIER_REGION = "healjai";

const Journey: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState<WorldRegion | null>(
    null,
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [openingRegion, setOpeningRegion] = useState<string | null>(null);
  // Purchased region plans (plan, start and renewal dates) keyed by region.
  const [regionSubs, setRegionSubs] = useState<Record<string, RegionSub>>({});

  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;
    api
      .get("/payment/subscriptions")
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, RegionSub> = {};
        for (const item of res.data?.regions || []) map[item.region] = item;
        setRegionSubs(map);
      })
      .catch(() => {
        // Cards fall back to the plain "Owned" / free-tier look.
      });
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  // The app shell scrolls its own container (App.tsx's scrollRef, not the
  // window) and never resets scroll position on route change, so arriving
  // here mid-scroll from another page can leave the region grid starting
  // below the fold — reads as a blank page until the user scrolls down.
  // Force that container back to the top whenever this page mounts or the
  // grid/checkout view is swapped.
  useEffect(() => {
    document
      .querySelector(".h-full.overflow-y-auto")
      ?.scrollTo({ top: 0 });
  }, [selectedRegion]);

  const groupedRegions = useMemo(() => getRegionsByContinent(), []);
  const activeValue = user?.region || "healjai";

  // A region counts as unlocked when it's the account's active region OR it's
  // in the purchased-regions list a Journey checkout adds to (see
  // buildRegionUnlockUpdate in backend/controllers/paymentController.js).
  const { unlockedRegions, lockedRegions } = useMemo(() => {
    const unlockedValues = [activeValue, ...(user?.unlockedRegions || [])];
    const all = groupedRegions.flatMap((group) => group.regions);
    // Only the Global plan opens every region; Pro keeps its own region.
    if (getUserPlan(user) === "global") {
      return { unlockedRegions: all, lockedRegions: [] as WorldRegion[] };
    }
    const unlocked: WorldRegion[] = [];
    const locked: WorldRegion[] = [];
    for (const region of all) {
      const owned = unlockedValues.some((v) => isSameRegion(region.value, v));
      (owned ? unlocked : locked).push(region);
    }
    return { unlockedRegions: unlocked, lockedRegions: locked };
  }, [groupedRegions, activeValue, user]);

  // Buying happens on Plans & Billing, priced for the region tapped here.
  const openCheckout = (region: WorldRegion) => {
    navigate(`/account/plans-billing?region=${encodeURIComponent(region.value)}`);
  };

  const closeCheckout = () => {
    setSelectedRegion(null);
    setCheckoutError("");
    setIsRedirecting(false);
  };

  // Switches the account's active region to an already-unlocked one and drops
  // the user into its content — same call RegionSettings.tsx uses to change
  // region, just triggered from here instead of Settings.
  const openRegion = async (region: WorldRegion) => {
    if (isSameRegion(region.value, activeValue)) {
      navigate("/");
      return;
    }
    setOpeningRegion(region.value);
    try {
      const res = await api.put(`/backend/user/${user?._id}`, {
        region: region.value,
      });
      const updated = res.data?.updatedUser || {};
      updateCurrentUser({
        region: updated.region || region.value,
        unlockedRegions: updated.unlockedRegions || user?.unlockedRegions,
      });
      navigate("/");
    } catch {
      setOpeningRegion(null);
    }
  };

  const handlePay = async () => {
    if (!selectedRegion) return;
    setCheckoutError("");
    setIsRedirecting(true);
    try {
      // /payment/checkout only accepts a planId — price and currency are read
      // from the database so a tampered request body can't change what the
      // user is charged. So the region + cadence picked here first has to be
      // resolved to the matching SubscriptionPlans document (seeded per
      // region by backend/scripts/seedRegionPlans.js) before checkout.
      const cadenceText = billingCycle === "monthly" ? "per month" : "per year";
      const plansRes = await api.get("/backend/subscriptionPlans");
      const plans =
        plansRes?.data?.data ||
        plansRes?.data?.plans ||
        plansRes?.data ||
        [];
      const plan = (Array.isArray(plans) ? plans : []).find(
        (p: any) =>
          p?.region === selectedRegion.value &&
          String(p?.billingCadence || "").toLowerCase() === cadenceText,
      );

      if (!plan?._id) {
        setCheckoutError(
          t("astriaHome.journey.noPlan", {
            cycle: t(
              billingCycle === "monthly"
                ? "astriaHome.journey.cycleMonthly"
                : "astriaHome.journey.cycleAnnual",
            ),
            region: t(
              `astriaHome.regionName.${selectedRegion.value}`,
              selectedRegion.label,
            ),
          }),
        );
        return;
      }

      const res = await api.post("/payment/checkout", { planId: plan._id });

      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setCheckoutError(t("astriaHome.journey.stripeMissing"));
    } catch (err: any) {
      setCheckoutError(
        err?.response?.data?.message ||
          err?.message ||
          t("astriaHome.journey.checkoutFailed"),
      );
    } finally {
      setIsRedirecting(false);
    }
  };

  if (selectedRegion) {
    return (
      <CheckoutPanel
        region={selectedRegion}
        billingCycle={billingCycle}
        onBillingCycleChange={setBillingCycle}
        onBack={closeCheckout}
        onPay={handlePay}
        isRedirecting={isRedirecting}
        error={checkoutError}
      />
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1100px] px-4 pb-6 pt-10">
        <div className="mx-auto max-w-[40rem] text-center">
          <div
            className="inline-flex items-center gap-2 uppercase"
            style={{
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              color: COLORS.amethyst,
            }}
          >
            {t("astriaHome.journey.eyebrow")}
          </div>
          <h1
            className="mt-[0.6rem] font-['Fraunces'] text-[2.1rem] font-bold tracking-[-0.01em]"
            style={{ color: COLORS.darkAmethyst }}
          >
            {t("astriaHome.journey.title")}
          </h1>
          <p
            className="mt-3 font-['Manrope'] text-[0.95rem] leading-[1.65]"
            style={{ color: "#C8BFD1" }}
          >
            {t("astriaHome.journey.summary", {
              unlocked: unlockedRegions.length,
              total: groupedRegions.reduce((n, g) => n + g.regions.length, 0),
            })}
          </p>
        </div>

        <PlanBanner className="mt-6" />

        {unlockedRegions.length > 0 ? (
          <div className="mx-auto mt-7 w-full max-w-[900px]">
            <SectionHeader
              title={t("astriaHome.journey.unlocked")}
              count={t("astriaHome.journey.regionCount", {
                count: unlockedRegions.length,
              })}
            />
            <div className="grid grid-cols-1 gap-[0.875rem] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {unlockedRegions.map((region) => (
                <UnlockedCard
                  key={region.value}
                  region={region}
                  isActive={isSameRegion(region.value, activeValue)}
                  isOpening={openingRegion === region.value}
                  sub={regionSubs[region.value]}
                  onOpen={() => openRegion(region)}
                  onUnlock={() => openCheckout(region)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {lockedRegions.length > 0 ? (
        <div className="mx-auto mt-7 w-full max-w-[900px]">
          <SectionHeader
            title={t("astriaHome.journey.explore")}
            count={t("astriaHome.journey.lockedCount", {
              count: lockedRegions.length,
            })}
          />

          <div className="grid grid-cols-1 gap-[0.875rem] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {lockedRegions.map((region) => (
              <RegionCard
                key={region.value}
                region={region}
                onUnlock={() => openCheckout(region)}
              />
            ))}
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string; count: string }> = ({
  title,
  count,
}) => (
  <div
    className="mb-4 flex items-baseline justify-between gap-4 border-b pb-[0.6rem]"
    style={{ borderColor: COLORS.hairline }}
  >
    <h2
      className="font-['Fraunces'] text-[1.15rem] font-bold"
      style={{ color: COLORS.darkAmethyst }}
    >
      {title}
    </h2>
    <span
      className="font-['Manrope'] text-[0.78rem] font-semibold"
      style={{ color: COLORS.muted }}
    >
      {count}
    </span>
  </div>
);

// Shared "card" look every region tile uses — 18px corners, a hairline
// border, and the lift-on-hover the rest of the site's tiles use (Worldwide
// chips, plan cards). Keeps this page from reading flatter than everywhere else.
const TILE_BASE =
  "group relative rounded-[18px] border bg-[#1A131F] p-4 text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(45,27,68,0.1)]";

// RegionCard is a motion.button so it can use whileHover/whileTap — Framer
// Motion drives `transform` itself once it's in play, which would silently
// win over a CSS hover:-translate-y class, so the lift goes through Framer
// Motion's own whileHover instead of Tailwind's hover: variant.
const TILE_HOVER = { y: -4, boxShadow: "0 12px 28px rgba(45,27,68,0.1)" };
const TILE_TAP = { y: -2 };

const formatCardDate = (value: string | undefined, locale: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const UnlockedCard: React.FC<{
  region: WorldRegion;
  isActive: boolean;
  isOpening: boolean;
  sub?: RegionSub;
  onOpen: () => void;
  onUnlock: () => void;
}> = ({ region, isActive, isOpening, sub, onOpen, onUnlock }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isPro = hasActiveSubscription(user);
  const isLight = useTheme().theme === "light";
  const isFreeTier = !sub && region.value === FREE_TIER_REGION;
  const startsLater = !!sub && new Date(sub.startDate).getTime() > Date.now();

  // Pro opens every region, so every card reads the same: no plan dates and no
  // per-region upsell. Only non-Pro accounts see plan, start and renewal info.
  const planLabel = isPro
    ? t("astriaHome.journey.includedPro", "Included in Pro")
    : sub
    ? t(
        sub.cadence === "annual"
          ? "astriaHome.journey.annualPlan"
          : "astriaHome.journey.monthlyPlan",
      )
    : isFreeTier
      ? t("astriaHome.journey.freeTier")
      : t("astriaHome.journey.owned");

  const rows: Array<[string, string]> = isPro
    ? []
    : sub
    ? [
        [
          t(startsLater ? "astriaHome.journey.starts" : "astriaHome.journey.started"),
          formatCardDate(sub.startDate, i18n.language),
        ],
        [
          t("astriaHome.journey.renews"),
          formatCardDate(sub.endDate, i18n.language),
        ],
      ]
    : isFreeTier
      ? [
          [t("astriaHome.journey.costLabel"), t("astriaHome.journey.costFree")],
          [t("astriaHome.journey.statusLabel"), t("astriaHome.journey.alwaysOn")],
        ]
      : [];

  return (
    <div
      className={`${TILE_BASE} flex flex-col`}
      style={{
        border: isActive
          ? `1.5px solid ${COLORS.amethyst}`
          : `1px solid ${COLORS.hairline}`,
        background: isActive
          ? isLight
            ? "linear-gradient(150deg, #EFEAFF 0%, #FFFFFF 100%)"
            : "linear-gradient(150deg, #221A29 0%, #1A131F 100%)"
          : "#1A131F",
        boxShadow: isActive
          ? isLight ? "0 10px 28px rgba(122,92,255,0.18)" : "0 10px 28px rgba(109,76,146,0.18)"
          : "0 4px 14px rgba(45,27,68,0.06)",
      }}
    >
      <span
        className="absolute right-3 top-0 inline-flex -translate-y-1/2 items-center gap-1 rounded-full px-2.5 py-0.5 font-['Manrope'] text-[9px] font-bold uppercase tracking-[0.12em] text-[#0F0A14]"
        style={{
          backgroundColor: isActive ? COLORS.amethyst : "#7DBFA6",
          boxShadow: "0 3px 10px rgba(45,27,68,0.22)",
        }}
      >
        <Check className="h-2.5 w-2.5" />
        {isActive
          ? t("astriaHome.journey.yourRegion")
          : t("astriaHome.journey.unlockedBadge")}
      </span>

      <div className="mt-2 flex items-center gap-2.5">
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-['Manrope'] text-sm font-semibold"
          style={{ background: COLORS.cream, color: COLORS.amethyst, border: "1px solid rgba(167,139,255,0.25)" }}
        >
          {String(t(`astriaHome.regionName.${region.value}`, region.label)).charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <div className="truncate font-['Manrope'] text-sm font-bold" style={{ color: COLORS.darkAmethyst }}>
            {t(`astriaHome.regionName.${region.value}`, region.label)}
          </div>
          <div className="truncate font-['Manrope'] text-[0.7rem] leading-[1.4]" style={{ color: COLORS.muted }}>
            {t(`astriaHome.regionTagline.${region.value}`, region.tagline)}
          </div>
        </span>
      </div>

      <div className="mt-[0.7rem] flex items-center justify-between gap-2">
        <span className="font-['Manrope'] text-[0.78rem] font-bold" style={{ color: "#7DBFA6" }}>
          {planLabel}
        </span>
        <button
          type="button"
          onClick={onOpen}
          disabled={isOpening}
          className="whitespace-nowrap rounded-full px-2.5 py-1 font-['Manrope'] text-[0.68rem] font-bold disabled:cursor-wait disabled:opacity-70"
          style={{ backgroundColor: "rgba(125,191,166,0.15)", color: "#7DBFA6" }}
        >
          {isOpening
            ? t("astriaHome.journey.opening")
            : isActive
              ? t("astriaHome.journey.open")
              : t("astriaHome.journey.switchTo")}
        </button>
      </div>

      {rows.length > 0 ? (
        <dl
          className="mt-3 space-y-1.5 border-t pt-3 font-['Manrope']"
          style={{ borderColor: COLORS.hairline }}
        >
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-2 text-[0.68rem]">
              <dt className="font-semibold uppercase tracking-[0.1em]" style={{ color: COLORS.muted }}>
                {label}
              </dt>
              <dd className="font-bold" style={{ color: isFreeTier ? "#7DBFA6" : COLORS.darkAmethyst }}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {isFreeTier && !isPro ? (
        <button
          type="button"
          onClick={onUnlock}
          className="mt-3 w-full rounded-full py-2 font-['Manrope'] text-[0.72rem] font-bold text-[#0F0A14] shadow-[0_0_18px_rgba(167,139,255,0.35)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: CTA_GRADIENT }}
        >
          {t("astriaHome.journey.unlock")}
        </button>
      ) : null}
    </div>
  );
};

const RegionCard: React.FC<{
  region: WorldRegion;
  onUnlock: () => void;
}> = ({ region, onUnlock }) => {
  const { t } = useTranslation();
  return (
    <motion.button
      type="button"
      onClick={onUnlock}
      whileHover={TILE_HOVER}
      whileTap={TILE_TAP}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-[18px] border bg-[#1A131F] p-4 text-left"
      style={{ borderColor: COLORS.hairline }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-['Manrope'] text-sm font-semibold"
          style={{ background: COLORS.cream, color: COLORS.amethyst, border: "1px solid rgba(167,139,255,0.25)" }}
        >
          {String(t(`astriaHome.regionName.${region.value}`, region.label)).charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <div className="truncate font-['Manrope'] text-sm font-bold" style={{ color: COLORS.darkAmethyst }}>
            {t(`astriaHome.regionName.${region.value}`, region.label)}
          </div>
          <div className="truncate font-['Manrope'] text-[0.7rem] leading-[1.4]" style={{ color: COLORS.muted }}>
            {t(`astriaHome.regionTagline.${region.value}`, region.tagline)}
          </div>
        </span>
      </div>

      <div className="mt-[0.7rem] flex items-center justify-between gap-2">
        <span className="font-['Manrope'] text-[0.78rem] font-bold" style={{ color: COLORS.darkAmethyst }}>
          {formatRegionPrice(region, region.monthlyPrice)}
          <small className="ml-0.5 font-medium" style={{ color: COLORS.muted }}>{t("astriaHome.journey.perMonthShort")}</small>
        </span>
        <span
          className="whitespace-nowrap rounded-full px-2.5 py-1 font-['Manrope'] text-[0.68rem] font-bold text-[#0F0A14] shadow-[0_0_18px_rgba(167,139,255,0.35)] transition-transform group-hover:scale-105"
          style={{ background: CTA_GRADIENT }}
        >
          {t("astriaHome.journey.unlock")}
        </span>
      </div>
    </motion.button>
  );
};

const STEPS = [
  "astriaHome.journey.steps.choose",
  "astriaHome.journey.steps.pay",
  "astriaHome.journey.steps.done",
];

const CheckoutPanel: React.FC<{
  region: WorldRegion;
  billingCycle: BillingCycle;
  onBillingCycleChange: (cycle: BillingCycle) => void;
  onBack: () => void;
  onPay: () => void;
  isRedirecting: boolean;
  error: string;
}> = ({
  region,
  billingCycle,
  onBillingCycleChange,
  onBack,
  onPay,
  isRedirecting,
  error,
}) => {
  const { t } = useTranslation();
  const regionName = t(`astriaHome.regionName.${region.value}`, region.label);
  const regionTagline = t(
    `astriaHome.regionTagline.${region.value}`,
    region.tagline,
  );
  const monthlyEquivalent = region.annualPrice / 12;
  const annualSavingsPct = region.monthlyPrice
    ? Math.round(100 - (monthlyEquivalent / region.monthlyPrice) * 100)
    : 0;
  const price =
    billingCycle === "monthly" ? region.monthlyPrice : region.annualPrice;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-4 pb-6 pt-8 md:px-8">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 font-['Manrope'] text-sm font-medium text-[#C8BFD1] transition hover:text-[#F7F3FA]"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("astriaHome.journey.cancel")}
          </button>
          <div className="inline-flex items-center gap-1.5 font-['Manrope'] text-xs text-[#C8BFD1]/75">
            <Lock className="h-3.5 w-3.5" />
            {t("astriaHome.journey.secure")}
          </div>
        </div>

        <div className="mt-8 text-center">
          <h1
            className="mt-3 font-['Fraunces'] text-3xl font-semibold"
            style={{ color: COLORS.ink }}
          >
            {t("astriaHome.journey.unlockRegion", { region: regionName })}
          </h1>
          <p className="mx-auto mt-2 max-w-md font-['Manrope'] text-sm text-[#C8BFD1]">
            {t("astriaHome.journey.chooseAccess")}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 font-['Manrope'] text-xs text-[#C8BFD1]/75">
          {STEPS.map((step, i) => (
            <React.Fragment key={step}>
              {i > 0 ? <span className="h-px w-8 bg-white/15" /> : null}
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{
                    backgroundColor: i === 0 ? COLORS.amethyst : "#221A29",
                    color: i === 0 ? "#0F0A14" : COLORS.muted,
                    border: i === 0 ? "none" : `1px solid ${COLORS.hairline}`,
                  }}
                >
                  {i + 1}
                </span>
                <span className={i === 0 ? "font-semibold text-[#F7F3FA]" : ""}>
                  {t(step)}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-[1fr_320px]">
          <div
            className="rounded-2xl bg-[#1A131F] p-5"
            style={{ border: `1px solid ${COLORS.hairline}` }}
          >
            <div className="font-['Manrope'] text-sm font-semibold text-[#F7F3FA]">
              {t("astriaHome.journey.billingOption")}
            </div>
            <p className="mt-1 font-['Manrope'] text-xs text-[#C8BFD1]/75">
              {t("astriaHome.journey.billingNote")}
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <BillingOption
                active={billingCycle === "monthly"}
                title={t("astriaHome.journey.monthly")}
                subtitle={t("astriaHome.journey.monthlySub")}
                price={t("astriaHome.journey.perMonth", {
                  price: formatRegionPrice(region, region.monthlyPrice),
                })}
                onSelect={() => onBillingCycleChange("monthly")}
              />
              <BillingOption
                active={billingCycle === "annual"}
                title={t("astriaHome.journey.annual")}
                badge={
                  annualSavingsPct > 0
                    ? t("astriaHome.journey.save", { pct: annualSavingsPct })
                    : undefined
                }
                subtitle={t("astriaHome.journey.annualSub")}
                price={t("astriaHome.journey.perYear", {
                  price: formatRegionPrice(region, region.annualPrice),
                })}
                onSelect={() => onBillingCycleChange("annual")}
              />
            </div>
          </div>

          <div
            className="rounded-2xl bg-[#1A131F] p-5"
            style={{ border: `1px solid ${COLORS.hairline}` }}
          >
            <div className="flex items-center gap-2 border-b pb-3" style={{ borderColor: COLORS.hairline }}>
              <div>
                <div className="font-['Manrope'] text-sm font-semibold text-[#F7F3FA]">
                  {regionName}
                </div>
                <div className="font-['Manrope'] text-xs text-[#C8BFD1]/75">
                  {regionTagline}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-between font-['Manrope'] text-sm text-[#C8BFD1]">
              <span>
                {billingCycle === "monthly"
                  ? t("astriaHome.journey.monthlyPlan")
                  : t("astriaHome.journey.annualPlan")}
              </span>
              <span className="font-semibold text-[#F7F3FA]">
                {formatRegionPrice(region, price)}
              </span>
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-4 font-['Manrope'] text-sm" style={{ borderColor: COLORS.hairline }}>
              <span className="font-semibold text-[#F7F3FA]">{t("astriaHome.journey.totalDue")}</span>
              <span className="text-lg font-bold text-[#F7F3FA]">
                {formatRegionPrice(region, price)}
              </span>
            </div>
            <p className="mt-1 font-['Manrope'] text-[11px] text-[#C8BFD1]/70">
              {t("astriaHome.journey.charged", { currency: region.currency })}
            </p>

            <button
              type="button"
              onClick={onPay}
              disabled={isRedirecting}
              className="mt-5 w-full rounded-full py-3 font-['Manrope'] text-sm font-semibold text-[#0F0A14] shadow-[0_0_22px_rgba(167,139,255,0.35)] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
              style={{ background: CTA_GRADIENT }}
            >
              {isRedirecting
                ? t("astriaHome.journey.redirecting")
                : t("astriaHome.journey.continuePay")}
            </button>
            <p className="mt-2 flex items-center justify-center gap-1.5 font-['Manrope'] text-[11px] text-[#C8BFD1]/70">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t("astriaHome.journey.stripeNote")}
            </p>

            {error ? (
              <p className="mt-3 text-center font-['Manrope'] text-xs text-red-600">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingOption: React.FC<{
  active: boolean;
  title: string;
  subtitle: string;
  price: string;
  badge?: string;
  onSelect: () => void;
}> = ({ active, title, subtitle, price, badge, onSelect }) => {
  const isLight = useTheme().theme === "light";
  const accent = isLight ? "#7A5CFF" : COLORS.amethyst;
  return (
  <button
    type="button"
    onClick={onSelect}
    className="flex items-center justify-between rounded-xl px-4 py-3.5 text-left transition-colors"
    style={{
      border: `1.5px solid ${active ? accent : isLight ? "#D9D9E6" : COLORS.hairline}`,
      backgroundColor: active
        ? isLight
          ? "rgba(122,92,255,0.10)"
          : "rgba(167,139,255,0.18)"
        : isLight
          ? "#FFFFFF"
          : "#1A131F",
      boxShadow: active ? `0 0 0 3px ${isLight ? "rgba(122,92,255,0.14)" : "rgba(167,139,255,0.18)"}` : undefined,
    }}
  >
    <div className="flex items-center gap-3">
      <span
        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full"
        style={{ border: `1.5px solid ${active ? accent : isLight ? "#B8B3CC" : "rgba(255,255,255,0.2)"}` }}
      >
        {active ? (
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: accent }}
          />
        ) : null}
      </span>
      <div>
        <div className="flex items-center gap-2 font-['Manrope'] text-sm font-semibold text-[#F7F3FA]">
          {title}
          {badge ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
              {badge}
            </span>
          ) : null}
        </div>
        <div className="font-['Manrope'] text-xs text-[#C8BFD1]/75">{subtitle}</div>
      </div>
    </div>
    <div className="font-['Manrope'] text-sm font-semibold text-[#F7F3FA]">
      {price}
    </div>
  </button>
  );
};

export default Journey;
